import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  SimpleChanges,
  OnChanges
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import {
  BroadcastService,
  Event,
  UserAuth,
  UserService,
  HelpMenuDialogService,
  UserIdleService
} from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';

export interface INavButton {
  id?: string;
  type: string;
  title: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: any;
  menuItems?: INavButton[];
  auth?: string;
  p;
}

@Component({
  selector: 'nrwl-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavbarComponent implements OnInit, OnChanges, OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() dataSource: any;
  @Input() homeUrl? = '/landing-page';
  @ViewChild('helpMenu') element: ElementRef;
  @ViewChild('menu') menu: ElementRef;
  showSubmenu = {};
  currentUser: UserAuth;
  shouldShowHelp = false;
  helpConfigData = {
    sources: [
      {
        url: '/doc/documentation/administrators/index.json'
      },
      {
        url: '/doc/documentation/users/index.json'
      },
      {
        url: '/certs/index.json'
      }
    ]
  };
  filePath = 'doc';
  private readonly unsubscribe: Subject<void> = new Subject();

  constructor(
    private readonly broadcastService: BroadcastService,
    private readonly userService: UserService,
    private readonly userAuth: UserAuth,
    private readonly router: Router,
    private readonly navigator: NavigationProvider,
    private readonly _UserIdleService: UserIdleService,
    private readonly helpMenuService: HelpMenuDialogService
  ) {}

  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
  @HostListener('document:click', ['$event']) onClick(event: Event) {
    if (
      this.element?.nativeElement.contains(event.target) ||
      this.menu?.nativeElement.contains(event.target)
    ) {
      this.shouldShowHelp = true;
    } else {
      this.shouldShowHelp = false;
    }
  }
  menuClick(itemType: string): void {
    this.showSubmenu[itemType] = true;
  }

  mouseLeave(itemType: string): void {
    this.showSubmenu[itemType] = false;
  }

  ngOnInit() {
    this.currentUser = this.userAuth.getUserAuth();
    this.userService.startDeltaInterval(); /* start delta service */
    this._UserIdleService.setUserIdleTime(true);
    this.helpMenuService.loadHelpSubMenuData(this.helpConfigData, false);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.dataSource && changes.dataSource.currentValue) {
      this.checkHideLayout();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
    this._UserIdleService.onExit();
  }

  checkHideLayout(): void {
    const filterData = [
      'licenses',
      'users_groups',
      'about',
      'uploads',
      'downloads',
      'reports_setting',
      'logs',
      'datasource_management',
      'endpoints_management',
      'dmz_server',
      'applications'
    ];
    if (this.userService.hideOtherLayout) {
      // show only logout and hide other menus
      const userSettings = this.dataSource.data?.find(data => data.type === 'user');
      if (userSettings && userSettings.menuItems) {
        const logoutData = userSettings.menuItems?.filter(item => item.id === 'logout');
        userSettings.menuItems = logoutData;
        this.dataSource.data = [userSettings];
      }
    }
    const settingData = this.dataSource?.data?.find(item => item.type === 'settings');
    if (settingData) {
      settingData.menuItems = settingData.menuItems.filter(item => filterData.includes(item.id));
    }
  }

  goHome(): void {
    if (this.userService.hideOtherLayout) {
      return;
    }
    // this navigation resets all parameters (eg time range is changed to LastHour, FF discarded, etc)
    this.router.navigate([this.homeUrl]);
  }

  toggleHelpMenu(value: boolean): void {
    this.shouldShowHelp = value;
  }

  openHelpLink(url: string): void {
    window.open(url, '_blank');
  }

  navigateOutlet(view: string[]): void {
    this.navigator.navigateToDashboard(view, {
      queryParams: { isFilterApplied: 'false' }
    });
  }

  // eslint-disable-next-line @typescript-eslint/no-inferrable-types
  onClickEvent(button: INavButton, menuOrigin: string = 'none'): void {
    this.showSubmenu[menuOrigin] = false;
    switch (button.id) {
      case 'users_groups': {
        sessionStorage.setItem('navigateIMSUrl', 'true');
        this.navigator.navigateToDashboard('user-administration', {
          queryParams: {
            showAlertsPreference: false,
            showThemePreference: true,
            outletParams: true
          }
        });
        break;
      }
      case 'profile': {
        this.navigator.navigateToDashboard('profile', {
          queryParams: { showAlertsPreference: false, showThemePreference: true }
        });
        break;
      }
      case 'logout': {
        this.userService.signout();
        break;
      }

      case 'monitor_management': {
        this.navigateOutlet(['monitor-management']);
        break;
      }
      case 'applications': {
        sessionStorage.removeItem('appSearch');
        this.navigateOutlet(['applications']);
        break;
      }
      case 'licenses': {
        this.navigateOutlet(['licenses']);
        break;
      }
      case 'about': {
        this.navigateOutlet(['about']);
        break;
      }
      case 'uploads': {
        this.navigateOutlet(['uploads']);
        break;
      }
      case 'downloads': {
        this.navigateOutlet(['downloads']);
        break;
      }
      case 'reports_setting': {
        this.navigateOutlet(['reports-setting']);
        break;
      }
      case 'logs': {
        this.navigateOutlet(['logs']);
        break;
      }
      case 'datasource_management': {
        this.navigateOutlet(['datasource-management']);
        break;
      }
      case 'endpoints_management': {
        this.navigateOutlet(['endpoints-management']);
        break;
      }
      case 'dmz_server': {
        this.navigateOutlet(['public-web-access']);
        break;
      }
      default: {
        if (typeof button.onClick === 'function') {
          button.onClick(button);
        }
        break;
      }
    }
  }

  getGravatarUrl(): string {
    return this.userService.getGravatarUrl(this.currentUser.data.EMAIL);
  }

  showLiveChat(): void {
    const event: Event = new Event('liveChatService', 'livechat:open', null);
    this.broadcastService.broadcastEvent(event);
  }

  trackByFn(index: number, item: INavButton): string {
    return item.id;
  }

  public isVisibleSubMenu(menu: INavButton): boolean {
    if (menu.id === 'profile') {
      return !this.userAuth.isLDAPUser;
    }
    return menu.auth ? this.userAuth.isAuthedFor(menu.auth) : true;
  }
}
